import { useRef, useEffect, useState } from 'react';
import { trackPhoneClick, trackLineClick } from '@/hooks/useAnalytics';

const faqs = [
  {
    q: '出張費・査定費はかかりますか？',
    a: 'いいえ、完全無料です。出張費・査定費・キャンセル料はすべて0円です。査定後にご納得いただけなかった場合も、一切費用はかかりません。お気軽にご依頼ください。',
  },
  {
    q: '査定後に断ることはできますか？',
    a: 'もちろんです。査定額にご納得いただけない場合は、その場でお断りいただけます。無理な買取交渉は一切行いませんのでご安心ください。',
  },
  {
    q: '対応エリアはどこですか？',
    a: '福岡・九州全域を中心に対応しております。中国・四国・近畿エリアにも出張いたします。遠方の場合もまずはお気軽にご相談ください。',
  },
  {
    q: '遺品整理にも対応していますか？',
    a: 'はい、対応しております。故人のお品物を丁寧に査定し、ご遺族の気持ちに寄り添った対応を心がけております。生前整理のご相談も承っております。',
  },
  {
    q: '買取できないものはありますか？',
    a: '状態や種類によってはお断りする場合がございます。また、偽造品・盗品などは法律により買取できません。事前にお電話やLINEでご相談いただくとスムーズです。',
  },
  {
    q: '支払いはいつ、どのような方法ですか？',
    a: '原則として査定当日に現金でお支払いいたします。ご希望の場合は銀行振込にも対応しております。即日現金払いが基本ですのでご安心ください。',
  },
  {
    q: '複数の品物をまとめて査定してもらえますか？',
    a: 'はい、何点でも対応可能です。ブランド品・貴金属・家電など複数ジャンルが混在していても一度にまとめて査定いたします。大量のお品物も歓迎です。',
  },
  {
    q: '予約から当日までの流れを教えてください。',
    a: 'お電話・WEBフォーム・LINEのいずれかでご連絡ください。ご希望の日時を調整後、スタッフがご自宅へ伺います。査定はその場で行い、ご納得いただければ即日現金でお支払いします。',
  },
];

export default function Faq() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="faq" className="py-24 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-amber-50/60 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-stone-50 -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-14">
          <span className={`text-amber-600 text-sm font-semibold tracking-widest uppercase mb-3 block transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            FAQ
          </span>
          <h2
            className={`text-4xl md:text-5xl font-black text-stone-900 mb-4 transition-all duration-700 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ fontFamily: '\'Noto Serif JP\', serif' }}
          >
            出張買取に関する<br />よくあるご質問
          </h2>
          <p className={`text-stone-500 text-base max-w-md mx-auto leading-relaxed transition-all duration-700 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            福岡・九州の出張買取についてよくいただくご質問をまとめました。<br />
            解決しない場合はお気軽にお問い合わせください。
          </p>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <FaqItem
              key={i}
              faq={faq}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              sectionVisible={titleVisible}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-14 text-center transition-all duration-700 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-stone-500 text-sm mb-5">解決しないご質問はお気軽にどうぞ</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="tel:0120-676-337"
              onClick={() => trackPhoneClick('faq')}
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill" />
              0120-676-337
            </a>
            <a
              href="https://lin.ee/OGQq9z6"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackLineClick('faq')}
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full font-bold text-sm transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap text-white"
              style={{ backgroundColor: '#06C755' }}
            >
              <i className="ri-line-line" />
              LINEで質問する
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FaqItem({
  faq,
  index,
  isOpen,
  onToggle,
  sectionVisible,
}: {
  faq: { q: string; a: string };
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  sectionVisible: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`border rounded-xl overflow-hidden transition-all duration-500 ${
        isOpen ? 'border-amber-300 bg-amber-50/50' : 'border-stone-200 bg-white hover:border-stone-300'
      } ${visible || sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-6 py-5 text-left cursor-pointer"
      >
        <span className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-sm font-black transition-colors ${
          isOpen ? 'bg-amber-500 text-white' : 'bg-stone-100 text-amber-600'
        }`}>
          Q
        </span>
        <span className={`flex-1 font-semibold text-sm md:text-base leading-snug transition-colors ${
          isOpen ? 'text-stone-900' : 'text-stone-800'
        }`}>
          {faq.q}
        </span>
        <span className={`shrink-0 w-6 h-6 flex items-center justify-center text-stone-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-lg" />
        </span>
      </button>

      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-60' : 'max-h-0'}`}>
        <div className="flex gap-4 px-6 pb-5">
          <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-stone-800 text-white text-sm font-black">
            A
          </span>
          <p className="text-stone-600 text-sm leading-relaxed pt-0.5">{faq.a}</p>
        </div>
      </div>
    </div>
  );
}
